import styled from "@emotion/styled"
import { InboxOutlined, SearchOutlined } from "@ant-design/icons"
import { Empty, Image, Input, Pagination, Table, Tag, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import { useMemo, useState } from "react"
import { InventoryStockData } from "services/interface"
import noImage from "assets/images/no-image.png"

const getStockImage = (record: InventoryStockData) =>
  record.product_image || record.product?.image || noImage

const getProductName = (record: InventoryStockData) =>
  record.product_name || record.product?.name || "-"

const getSkuLabel = (record: InventoryStockData) =>
  record.product_sku_name ||
  record.product_sku?.warehouse_sku_name ||
  record.sku_variant_name ||
  record.sku_variant?.sku_value ||
  "สินค้า"

interface GroupedProduct {
  productId: string
  productName: string
  productImage: string
  department?: InventoryStockData["department"]
  skus: InventoryStockData[]
  totalQty: number
}

interface StockTableProps {
  data: InventoryStockData[]
  loading?: boolean
  size?: "small" | "middle" | "large"
  showDepartment?: boolean
  pageSize?: number
  showSearch?: boolean
}

const DEFAULT_PAGE_SIZE = 5

const StockTable = ({
  data,
  loading,
  size = "small",
  showDepartment = false,
  pageSize = DEFAULT_PAGE_SIZE,
  showSearch = false,
}: StockTableProps) => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")

  // จัดกลุ่ม stock ตามสินค้า (1 สินค้า = 1 การ์ด, ภายในมีหลาย SKU) ให้เหมือนหน้า admin
  const groups = useMemo<GroupedProduct[]>(() => {
    const map = new Map<string, GroupedProduct>()
    ;(data ?? []).forEach((stock) => {
      const key = stock.product_id || stock.id
      const existing = map.get(key)
      if (existing) {
        existing.skus.push(stock)
        existing.totalQty += stock.qty_onhand ?? 0
      } else {
        map.set(key, {
          productId: key,
          productName: getProductName(stock),
          productImage: getStockImage(stock),
          department: stock.department,
          skus: [stock],
          totalQty: stock.qty_onhand ?? 0,
        })
      }
    })
    return Array.from(map.values())
  }, [data])

  // กรองตามคำค้นหา: จับทั้งชื่อสินค้าและชื่อตัวเลือก (SKU)
  // ถ้าคำค้นตรงเฉพาะบาง SKU จะแสดงสินค้านั้นเฉพาะ SKU ที่ตรง
  const filteredGroups = useMemo<GroupedProduct[]>(() => {
    const keyword = search.trim().toLowerCase()
    if (!keyword) return groups
    return groups.flatMap((group) => {
      if (group.productName.toLowerCase().includes(keyword)) return [group]
      const matchedSkus = group.skus.filter((sku) =>
        getSkuLabel(sku).toLowerCase().includes(keyword)
      )
      if (matchedSkus.length === 0) return []
      return [
        {
          ...group,
          skus: matchedSkus,
          totalQty: matchedSkus.reduce(
            (sum, sku) => sum + (sku.qty_onhand ?? 0),
            0
          ),
        },
      ]
    })
  }, [groups, search])

  if (!loading && groups.length === 0) {
    return (
      <Empty
        description="ไม่มีข้อมูลสต็อก"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ padding: 24 }}
      />
    )
  }

  const totalPages = Math.ceil(filteredGroups.length / pageSize) || 1
  const safePage = Math.min(page, totalPages)
  const start = (safePage - 1) * pageSize
  const pageGroups = filteredGroups.slice(start, start + pageSize)

  const skuColumns: ColumnsType<InventoryStockData> = [
    {
      title: "ตัวเลือกสินค้า",
      key: "sku",
      render: (_: unknown, record: InventoryStockData) => (
        <SkuCell>
          <SkuIcon>
            <InboxOutlined />
          </SkuIcon>
          <Typography.Text>{getSkuLabel(record)}</Typography.Text>
        </SkuCell>
      ),
    },
    {
      title: "จำนวนสินค้า",
      dataIndex: "qty_onhand",
      key: "qty_onhand",
      align: "center" as const,
      width: 140,
      render: (value: number) => (
        <Typography.Text strong>{value ?? 0}</Typography.Text>
      ),
    },
  ]

  return (
    <Wrapper>
      {showSearch && (
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          placeholder="ค้นหาสินค้า / ตัวเลือกสินค้า"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          style={{ maxWidth: 320 }}
        />
      )}

      {filteredGroups.length === 0 && (
        <Empty
          description="ไม่พบสินค้าที่ค้นหา"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: 24 }}
        />
      )}

      {pageGroups.map((group) => (
        <ProductCard key={group.productId}>
          <ProductHeader>
            <Image
              src={group.productImage}
              fallback={noImage}
              width={44}
              height={44}
              style={{ objectFit: "cover", borderRadius: 8 }}
              preview={{ src: group.productImage }}
            />
            <ProductInfo>
              <Typography.Text strong style={{ fontSize: 15 }}>
                {group.productName}
              </Typography.Text>
              {showDepartment && group.department?.description && (
                <Tag color="blue" style={{ width: "fit-content", marginTop: 2 }}>
                  {group.department.description}
                </Tag>
              )}
            </ProductInfo>
            <ProductTotal>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                รวมคงเหลือ
              </Typography.Text>
              <Typography.Text strong style={{ fontSize: 16 }}>
                {group.totalQty}
              </Typography.Text>
            </ProductTotal>
          </ProductHeader>
          <Table
            rowKey="id"
            columns={skuColumns}
            dataSource={group.skus}
            loading={loading}
            size={size}
            pagination={false}
          />
        </ProductCard>
      ))}

      {filteredGroups.length > pageSize && (
        <PaginationRow>
          <Pagination
            current={safePage}
            pageSize={pageSize}
            total={filteredGroups.length}
            onChange={setPage}
            size="small"
            showSizeChanger={false}
          />
        </PaginationRow>
      )}
    </Wrapper>
  )
}

export default StockTable

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ProductCard = styled.div`
  border: 1px solid #efefef;
  border-radius: 8px;
  overflow: hidden;
`

const ProductHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background-color: #fafafa;
  border-bottom: 1px solid #efefef;
`

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const ProductTotal = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  white-space: nowrap;
`

const SkuCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const SkuIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 6px;
  background-color: #f5f5f5;
  color: #8c8c8c;
  font-size: 18px;
  flex-shrink: 0;
`

const PaginationRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
`
